import { useState } from "react";
import { Sparkles, X, RefreshCw, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { getAIResponse } from "@/services/aiService";
import { toast } from "sonner";

interface AIAssistantProps {
  fieldLabel?: string;
  currentValue?: string;
  onSelect: (content: string) => void;
  context?: string;
}

export function AIAssistant({ fieldLabel, currentValue, onSelect, context }: AIAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [responses, setResponses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const generatePrompt = (basePrompt: string, additional?: string) => {
    let prompt = basePrompt;
    if (context) {
      prompt = `Context: ${context}\n\n${prompt}`;
    }
    if (additional?.trim()) {
      prompt = `${prompt}\n\nAdditional requirements: ${additional}`;
    }
    return prompt;
  };

  const generateResponses = async () => {
    setIsLoading(true);
    setResponses([]);

    const basePrompt = fieldLabel 
      ? `Generate content for "${fieldLabel}" field. ${currentValue ? `Current content: ${currentValue.substring(0, 200)}...` : ''}`
      : "Generate helpful content";

    try {
      const prompts = [
        generatePrompt(basePrompt, additionalPrompt),
        generatePrompt(`${basePrompt}. Make it more detailed and comprehensive.`, additionalPrompt),
        generatePrompt(`${basePrompt}. Make it concise and professional.`, additionalPrompt),
      ];

      const results = await Promise.all(
        prompts.map(prompt => getAIResponse(prompt))
      );

      const newResponses = results.map(result => 
        result.success ? result.content : "Failed to generate response"
      );

      setResponses(newResponses);
    } catch (error) {
      console.error("Error generating AI responses:", error);
      toast.error("Failed to generate AI responses");
      setResponses(["Error generating response", "Error generating response", "Error generating response"]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleUseResponse = (content: string) => {
    onSelect(content);
    setIsOpen(false);
    toast.success("Content applied");
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setIsOpen(true);
          if (responses.length === 0) {
            generateResponses();
          }
        }}
        className="h-8 px-2 bg-white hover:bg-gray-50 border shadow-sm"
        title="AI Assistant"
      >
        AI
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setIsOpen(false)} />
          <div className="fixed top-4 right-4 z-50 w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-blue-600" />
                AI Assistant
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Additional Prompt (Optional)</label>
                <Input
                  value={additionalPrompt}
                  onChange={(e) => setAdditionalPrompt(e.target.value)}
                  placeholder="e.g., Make it more formal, Add bullet points..."
                  className="text-sm"
                />
              </div>

              <Button
                onClick={generateResponses}
                disabled={isLoading}
                className="w-full"
                variant="outline"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate Responses
                  </>
                )}
              </Button>

              {responses.length > 0 && (
                <div className="space-y-3">
                  {responses.map((response, index) => (
                    <Card key={index} className="border-2">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-xs font-medium text-muted-foreground">
                            Option {index + 1}
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopy(response, index)}
                              className="h-7 w-7 p-0"
                            >
                              {copiedIndex === index ? (
                                <Check className="h-3 w-3 text-green-600" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap mb-3 max-h-48 overflow-y-auto">
                          {response}
                        </div>
                        <Button
                          onClick={() => handleUseResponse(response)}
                          size="sm"
                          className="w-full"
                          variant="default"
                        >
                          Use This
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {isLoading && responses.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p>Generating AI responses...</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

